import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { IN_HOME_USER_EVENTS } from "../lib/events";
import { useSubmitScreeningSurvey } from "./api/useSubmitScreeningSurvey";
import { useScreeningSurveyPersistedState } from "./hooks/useScreeningSurveyPersistedState";
import {
  EMR_OTHER_VALUE,
  type ScreeningSurveyFormData,
  screeningSurveyFormSchema,
} from "./lib/screeningSurveySchema";
import { ScreeningSurveyWelcome } from "./ScreeningSurveyWelcome";
import { EmrSystemsStep } from "./steps/EmrSystemsStep";
import { HasCarStep } from "./steps/HasCarStep";
import { HomeHealthExperienceStep } from "./steps/HomeHealthExperienceStep";
import { HospiceExperienceStep } from "./steps/HospiceExperienceStep";
import { LanguagesStep } from "./steps/LanguagesStep";
import { OasisAssessmentsStep } from "./steps/OasisAssessmentsStep";
import { SpecialtyCareStep } from "./steps/SpecialtyCareStep";
import { WhyHealthcareStep } from "./steps/WhyHealthcareStep";

const STEP_NAMES = [
  "welcome",
  "homeHealthExperience",
  "hospiceExperience",
  "hasCar",
  "emrSystems",
  "oasisAssessments",
  "languages",
  "specialtyCare",
  "whyHealthcare",
] as const;

const TOTAL_STEPS = STEP_NAMES.length - 1;

interface ScreeningSurveyFlowProps {
  onComplete: () => void;
}

export function ScreeningSurveyFlow(props: ScreeningSurveyFlowProps) {
  const { onComplete } = props;
  const { userId: workerId } = useDefinedWorker();
  const { showErrorToast } = useToast();

  const { persistedState, persistStepIndex, persistFormValues, clearPersistedState } =
    useScreeningSurveyPersistedState(workerId);

  const [stepIndex, setStepIndex] = useState(persistedState.stepIndex);

  const methods = useForm<ScreeningSurveyFormData>({
    resolver: zodResolver(screeningSurveyFormSchema),
    defaultValues: {
      emrSystems: [],
      emrSystemsOther: "",
      languages: [],
      specialtyCareExperience: [],
      ...persistedState.formValues,
    },
  });

  useEffect(() => {
    persistStepIndex(stepIndex);
  }, [stepIndex, persistStepIndex]);

  const { watch } = methods;
  useEffect(() => {
    const subscription = watch((values) => {
      persistFormValues(values as Partial<ScreeningSurveyFormData>);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [watch, persistFormValues]);

  const submitMutation = useSubmitScreeningSurvey(workerId, {
    onSuccess: (data) => {
      const {
        data: {
          attributes: { status },
        },
      } = data;
      logEvent(
        status === "PASSED"
          ? IN_HOME_USER_EVENTS.SCREENING_SURVEY_PASSED
          : IN_HOME_USER_EVENTS.SCREENING_SURVEY_WAITLISTED
      );
      clearPersistedState();
      onComplete();
    },
  });

  const goForward = useCallback(() => {
    logEvent(IN_HOME_USER_EVENTS.SCREENING_SURVEY_STEP_COMPLETED, {
      step: stepIndex,
      stepName: STEP_NAMES[stepIndex],
    });
    setStepIndex((previous) => previous + 1);
  }, [stepIndex]);

  const goBack = useCallback(() => {
    setStepIndex((previous) => Math.max(0, previous - 1));
  }, []);

  const handleGetStarted = useCallback(() => {
    logEvent(IN_HOME_USER_EVENTS.SCREENING_SURVEY_STARTED);
    setStepIndex(1);
  }, []);

  const handleSubmit = useCallback(() => {
    void methods.handleSubmit(
      (formData) => {
        logEvent(IN_HOME_USER_EVENTS.SCREENING_SURVEY_SUBMITTED);
        const { emrSystemsOther, ...rest } = formData;
        const otherText = emrSystemsOther?.trim();
        const emrSystems =
          formData.emrSystems.includes(EMR_OTHER_VALUE) && otherText
            ? formData.emrSystems.map((value) =>
                value === EMR_OTHER_VALUE ? `Other: ${otherText}` : value
              )
            : formData.emrSystems;
        submitMutation.mutate({ ...rest, emrSystems });
      },
      () => {
        showErrorToast("Please complete all required fields before submitting.");
      }
    )();
  }, [methods, submitMutation, showErrorToast]);

  if (stepIndex === 0) {
    return <ScreeningSurveyWelcome onGetStarted={handleGetStarted} />;
  }

  const stepProps = {
    currentStep: stepIndex - 1,
    totalSteps: TOTAL_STEPS,
    onContinue: stepIndex === TOTAL_STEPS ? handleSubmit : goForward,
    onBack: goBack,
  };

  return (
    <FormProvider {...methods}>
      {stepIndex === 1 && <HomeHealthExperienceStep {...stepProps} />}
      {stepIndex === 2 && <HospiceExperienceStep {...stepProps} />}
      {stepIndex === 3 && <HasCarStep {...stepProps} />}
      {stepIndex === 4 && <EmrSystemsStep {...stepProps} />}
      {stepIndex === 5 && <OasisAssessmentsStep {...stepProps} />}
      {stepIndex === 6 && <LanguagesStep {...stepProps} />}
      {stepIndex === 7 && <SpecialtyCareStep {...stepProps} />}
      {stepIndex === 8 && (
        <WhyHealthcareStep {...stepProps} isSubmitting={submitMutation.isLoading} />
      )}
    </FormProvider>
  );
}
